import {
	CANONICAL_SCHEMAS,
	type CanonicalSchemaRef,
	type FixedWidthField,
	type ImportDefinitionContract,
	type ImportDefinitionStatus,
	type ImportDesignerInspection,
	type ImportDesignerSample,
	type ImportFormat,
	ImportFormatType,
	ImportLineEnding,
	ImportNotificationAction,
	ImportOnErrorStrategy,
	ImportProcessingStrategy,
	ImportSourceEncoding,
	ImportSourceFieldType,
	KEYED_STRATEGIES,
	LogicalFileType,
	type MappingField,
	type RecordValidation,
	SOURCE_SYSTEM_PATTERN,
} from '@today/types'
import { boundariesOf, type SourceFieldRow, segmentsOf, toggleBoundary } from '@today/ui-domain'

// The designer's state and the F5 contract it builds (TO-400 design D7, BR-12,
// BR-13). Pure: the hook holds the state, these functions change it.

type Processing = ImportDefinitionContract['processing']

/** The designer's steps, in order (spec Happy path). */
export enum DesignerStep {
	TARGET = 'target',
	SAMPLE = 'sample',
	FIELDS = 'fields',
	MAPPING = 'mapping',
	PROCESSING = 'processing',
	PREVIEW = 'preview',
	DRY_RUN = 'dry-run',
	SAVE = 'save',
}

/** The version the designer was opened from, when it edits an existing one. */
export interface DesignerBase {
	version: number
	status: ImportDefinitionStatus
	revision: number
}

export interface DesignerState {
	key: string
	logicalFile: LogicalFileType
	canonicalSchema: CanonicalSchemaRef
	contract: ImportDefinitionContract
	/** Fixed-width only: the ruler's boundaries and line width. */
	boundaries: number[]
	lineLength: number
	sample: ImportDesignerSample | null
	inspection: ImportDesignerInspection | null
	base: DesignerBase | null
}

/** The schema each logical file targets by default: the newest version of its element type. */
const SCHEMA_OF_FILE: Record<LogicalFileType, string> = {
	[LogicalFileType.ORGANIZATIONAL]: 'organization-unit',
	[LogicalFileType.CLIENTS]: 'client',
	[LogicalFileType.CONVERSIONS]: 'conversion',
	[LogicalFileType.USERS]: 'user',
	[LogicalFileType.PRODUCTS]: 'product',
}

export const defaultSchemaOf = (file: LogicalFileType): CanonicalSchemaRef => {
	const name = SCHEMA_OF_FILE[file]
	const version = Math.max(...CANONICAL_SCHEMAS.filter((s) => s.name === name).map((s) => s.version))
	return { name, version }
}

const DEFAULT_PROCESSING: Processing = {
	strategy: ImportProcessingStrategy.UPSERT,
	key: [],
	atomic: true,
	onError: { strategy: ImportOnErrorStrategy.REJECT_FILE },
	sourceSystem: null,
}

/** A new contract with the defaults F5 accepts once mapped (design D7). */
const newContract = (canonicalSchema: CanonicalSchemaRef): ImportDefinitionContract => ({
	canonicalSchema,
	file: {
		naming: { pattern: '^.+$' },
		format: {
			type: ImportFormatType.CSV,
			encoding: ImportSourceEncoding.UTF_8,
			lineEnding: ImportLineEnding.AUTO,
			skipLines: 0,
			delimiter: ',',
			quote: '"',
			header: true,
			fields: [],
		},
	},
	mapping: { fields: [] },
	validation: { record: [], dataset: [] },
	processing: { ...DEFAULT_PROCESSING },
	notification: { missingFile: ImportNotificationAction.NONE, invalidFile: ImportNotificationAction.NONE },
	retention: { originalFileDays: 90, reportsDays: 90 },
})

export const newDesigner = (logicalFile = LogicalFileType.ORGANIZATIONAL): DesignerState => {
	const canonicalSchema = defaultSchemaOf(logicalFile)
	return {
		key: '',
		logicalFile,
		canonicalSchema,
		contract: newContract(canonicalSchema),
		boundaries: [],
		lineLength: 0,
		sample: null,
		inspection: null,
		base: null,
	}
}

const fixedEnd = (fields: readonly FixedWidthField[]) => Math.max(0, ...fields.map((f) => f.start + f.length - 1))

/** The designer over a stored version (spec Alternative paths): a sample is needed again before preview. */
export const designerOf = (
	key: string,
	contract: ImportDefinitionContract,
	base: DesignerBase,
	logicalFile = LogicalFileType.ORGANIZATIONAL,
): DesignerState => {
	const { format } = contract.file
	const fixed = format.type === ImportFormatType.FIXED_WIDTH
	return {
		...newDesigner(logicalFile),
		key,
		canonicalSchema: contract.canonicalSchema,
		contract,
		boundaries: fixed ? boundariesOf(format.fields) : [],
		lineLength: fixed ? fixedEnd(format.fields) : 0,
		base,
	}
}

/** The contract Save sends and the JSON view shows (BR-12): the state's own contract, unchanged. */
export const contractOf = (state: DesignerState): ImportDefinitionContract => state.contract

const withContract = (state: DesignerState, patch: Partial<ImportDefinitionContract>): DesignerState => ({
	...state,
	contract: { ...state.contract, ...patch },
})

const withFormat = (state: DesignerState, format: ImportFormat): DesignerState =>
	withContract(state, { file: { ...state.contract.file, format } })

export const selectLogicalFile = (state: DesignerState, logicalFile: LogicalFileType): DesignerState => {
	const canonicalSchema = defaultSchemaOf(logicalFile)
	return { ...withContract(state, { canonicalSchema }), logicalFile, canonicalSchema }
}

export const selectSchema = (state: DesignerState, canonicalSchema: CanonicalSchemaRef): DesignerState => ({
	...withContract(state, { canonicalSchema }),
	canonicalSchema,
})

export const setNaming = (state: DesignerState, pattern: string): DesignerState =>
	withContract(state, { file: { ...state.contract.file, naming: { pattern } } })

/**
 * A sample was inspected: its suggested format replaces the current one, except
 * when an existing contract of the same format type is being edited — then the
 * edited fields are kept and only the ruler width comes from the sample.
 */
export const applyInspection = (
	state: DesignerState,
	sample: ImportDesignerSample,
	inspection: ImportDesignerInspection,
): DesignerState => {
	const lineLength = inspection.lineLength ?? 0
	const keep = state.base !== null && state.contract.file.format.type === inspection.format.type
	const next = { ...state, sample, inspection, lineLength: Math.max(lineLength, keep ? state.lineLength : 0) }
	if (keep) return next
	const { format } = inspection
	// A fixed-width sample starts as one field over the whole line until the ruler cuts it.
	const fresh =
		format.type === ImportFormatType.FIXED_WIDTH ? { ...format, fields: fieldsOfRuler([], [], lineLength) } : format
	return { ...withFormat(next, fresh), boundaries: [] }
}

/**
 * BR-13: the fixed-width fields the ruler cuts. A segment keeps the name and
 * type of the field that started (else ended) at the same column, so moving a
 * boundary keeps the renamed fields around it; a new segment is `field_<n>`.
 */
const fieldsOfRuler = (previous: readonly FixedWidthField[], boundaries: readonly number[], lineLength: number) =>
	segmentsOf(boundaries, lineLength).map((segment, i) => {
		const end = segment.start + segment.length - 1
		const match =
			previous.find((f) => f.start === segment.start) ?? previous.find((f) => f.start + f.length - 1 === end)
		return {
			name: match?.name ?? `field_${i + 1}`,
			type: match?.type ?? ImportSourceFieldType.STRING,
			required: match?.required ?? false,
			...segment,
		}
	})

export const toggleRulerColumn = (state: DesignerState, column: number): DesignerState => {
	const { format } = state.contract.file
	if (format.type !== ImportFormatType.FIXED_WIDTH) return state
	const boundaries = toggleBoundary(state.boundaries, column, state.lineLength)
	const fields = fieldsOfRuler(format.fields, boundaries, state.lineLength)
	return { ...withFormat(state, { ...format, fields }), boundaries }
}

const locationOf = (format: ImportFormat, i: number): string => {
	if (format.type === ImportFormatType.FIXED_WIDTH) {
		const f = format.fields[i] as FixedWidthField
		return `${f.start}–${f.start + f.length - 1}`
	}
	if (format.type === ImportFormatType.CSV) {
		const f = format.fields[i]
		return f?.column !== null && f?.column !== undefined ? `column ${f.column}` : `position ${f?.position}`
	}
	return format.fields[i]?.path ?? ''
}

export const sourceRowsOf = (state: DesignerState): SourceFieldRow[] => {
	const { format } = state.contract.file
	return format.fields.map((f, i) => ({
		name: f.name,
		type: f.type,
		required: f.required,
		location: locationOf(format, i),
	}))
}

/** Renames, retypes or flags one source field; a rename follows into the mapping's sources. */
export const editSourceField = (
	state: DesignerState,
	index: number,
	edit: Pick<SourceFieldRow, 'name' | 'type' | 'required'>,
): DesignerState => {
	const { format } = state.contract.file
	const before = format.fields[index]?.name
	// Only the three editable properties: a view row's display-only extras never reach the contract.
	const { name, type, required } = edit
	const fields = format.fields.map((f, i) =>
		i === index ? { ...f, name, type, required } : f,
	) as ImportFormat['fields']
	const mapping = state.contract.mapping.fields.map((m) => ({
		...m,
		sources: m.sources.map((s) => (s === before ? name : s)),
	}))
	return withContract(withFormat(state, { ...format, fields } as ImportFormat), { mapping: { fields: mapping } })
}

export const setMapping = (state: DesignerState, fields: MappingField[]): DesignerState =>
	withContract(state, { mapping: { fields } })

export const setRecordRules = (state: DesignerState, record: RecordValidation[]): DesignerState =>
	withContract(state, { validation: { ...state.contract.validation, record } })

export const setProcessing = (state: DesignerState, processing: Processing): DesignerState =>
	withContract(state, { processing })

/** What a keyed strategy still lacks before preview, dry-run or save (SCN-009). */
export const missingProcessing = (state: DesignerState): ('key' | 'sourceSystem')[] => {
	const { processing } = state.contract
	if (!KEYED_STRATEGIES.has(processing.strategy)) return []
	const missing: ('key' | 'sourceSystem')[] = []
	if (processing.key.length === 0) missing.push('key')
	if (!SOURCE_SYSTEM_PATTERN.test(processing.sourceSystem ?? '')) missing.push('sourceSystem')
	return missing
}

/** The step a contract violation path belongs to, so the message lands next to its cause. */
export const stepOfPath = (path: string): DesignerStep => {
	if (path.startsWith('contract.file.format')) return DesignerStep.FIELDS
	if (path.startsWith('contract.file')) return DesignerStep.TARGET
	if (path.startsWith('contract.mapping') || path.startsWith('contract.validation')) return DesignerStep.MAPPING
	if (path.startsWith('contract.processing')) return DesignerStep.PROCESSING
	return DesignerStep.TARGET
}
